import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Award, CheckCircle2, Wheat, Wind } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import TermRichText from "@/components/TermRichText";
import { usePageMeta } from "@/hooks/usePageMeta";

const BLUEPRINT_URL =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/bt5jlaef_blueprint_detailed.webp";

const SPECS = [
  { label: "System", value: "TP 24" },
  { label: "Capacity", value: "24 TPD (tonnes per day)" },
  { label: "Footprint", value: "25 × 50 ft" },
  { label: "Power Consumption", value: "~40 kW" },
  {
    label: "Output",
    value: "Biochar ~27%, Bio-oil ~16%, plus syngas",
    footnote:
      "Output yields shown for cashew shell cake feedstock; actual yields vary by feedstock type.",
  },
  { label: "Automation", value: "Fully automatic — PLC/SCADA controlled" },
];

// Brand colors (matching /app/frontend/tailwind.config.js)
const COLOR_PAPER = [250, 249, 247];

// Company overview content (pulled directly from About page)
const ABOUT_EYEBROW = "About Tanren";

// Additional Technology products, rendered in the same layout as TP 24.
const TORREFACTION_BODY =
  "Torrefaction is mild pyrolysis \u2014 biomass is heated in a low-oxygen environment to around 200\u2013300\u00B0C, just enough to drive off moisture and volatiles without fully carbonizing the material. What comes out is torrefied biomass: denser, more uniform, hydrophobic, and with a higher calorific value than raw biomass \u2014 easier to store, grind, and transport, and a straightforward drop-in substitute for coal in co-firing applications.";

const TP24_BODY =
  "Designed and operated by a team that ran this technology themselves before offering it for sale.";

const CBG_BODY =
  "Compressed Biogas (CBG) is renewable methane produced by upgrading biogas from organic waste. Built on proven, licensed technology, our 50\u20131,000 kg/day skid-based systems enable distributed production close to the feedstock source, with modular capacity and simpler deployment.";

const CONFIGURATIONS = [
  { capacity: "10 TPD", line: "Biochar only" },
  { capacity: "24 TPD", line: "Biochar only" },
  { capacity: "24 TPD", line: "Biochar & bio-oil" },
];

const TECHNOLOGIES = [
  { number: "01", title: "Biomass Pyrolysis", body: TP24_BODY, href: "#pyrolysis" },
  { number: "02", title: "Torrefaction Plant", body: TORREFACTION_BODY, href: "#torrefaction-plant" },
  { number: "03", title: "Modular CBG System", body: CBG_BODY },
];

const PRODUCTS = [
  {
    id: "torrefaction-plant",
    title: "Torrefaction Plant",
    body: TORREFACTION_BODY,
    specs: [{ label: "Input feed capacity", value: "1\u20136 TPH" }],
  },
];

const Equipment = () => {
  usePageMeta(
    "Biomass Pyrolysis | Tanren",
    "Pyrolysis systems in 10 and 24 TPD capacities, engineered to produce EBC-AgroOrganic grade biochar and proven in commercial operation. View specs and request a quote."
  );
  return (
  <div data-testid="page-equipment">
    {/* ENGINEERING CREDIBILITY — compact page opening */}
    <section
      data-testid="equipment-why"
      className="bg-paper pt-36 pb-14 lg:pb-16 border-b border-char-900/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14">
        <ScrollReveal className="md:col-span-5">
          <div className="mono-label">Why buy from us</div>
          <h2 className="font-display mt-4 text-char-900">
            Engineering that has held up in operation, not just on paper.
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={120} className="md:col-span-7 space-y-6 text-lg text-char-700 leading-relaxed">
          <p>
            Most equipment sellers ask you to trust a spec sheet. For pyrolysis, we're asking you
            to trust a team that has run this exact technology in commercial operation — the same
            people who designed it are the ones who commissioned, operated, and optimized it.
          </p>
          <p className="text-char-900">
            That's the difference between engineering on paper and engineering that holds up.
          </p>
        </ScrollReveal>
      </div>
    </section>

    {/* TECHNOLOGY OVERVIEW — all four technologies, with anchor navigation */}
    <section data-testid="equipment-overview" className="bg-paper py-14 lg:py-16 border-b border-char-900/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TECHNOLOGIES.map((t) => {
            const inner = (
              <div className="h-full flex flex-col border border-char-900/10 bg-paper hover:border-char-900/25 transition-colors p-6">
                <div className="flex items-start justify-between">
                  <span className="numeral text-2xl">{t.number}</span>
                  {(t.href || t.to) && (
                    <ArrowRight
                      size={16}
                      className="text-char-700 mt-1.5"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <h3 className="mt-5 font-display text-char-900 leading-tight">
                  {t.title}
                </h3>
                {t.tag && (
                  <div className="mt-2">
                    <span
                      data-testid={`overview-card-${t.number}-tag`}
                      className="inline-block font-sans text-[11px] uppercase tracking-[0.12em] text-char-700 border border-char-900/20 px-2 py-0.5 whitespace-nowrap"
                    >
                      {t.tag}
                    </span>
                  </div>
                )}
                <p className="mt-3 text-char-700 text-[15px] leading-relaxed line-clamp-3">
                  {t.body}
                </p>
              </div>
            );
            if (t.to)
              return (
                <Link key={t.number} to={t.to} data-testid={`overview-card-${t.number}`}>
                  {inner}
                </Link>
              );
            if (t.href)
              return (
                <a key={t.number} href={t.href} data-testid={`overview-card-${t.number}`}>
                  {inner}
                </a>
              );
            return (
              <div key={t.number} data-testid={`overview-card-${t.number}`} className="sm:col-span-2 lg:col-span-1">
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* TP 24 — DETAIL OPENER */}
    <section
      id="pyrolysis"
      data-testid="equipment-hero"
      className="bg-paper text-char-900 py-16 lg:py-20 scroll-mt-28"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <h1 className="font-display max-w-[22ch] text-char-900">
          <span>Biomass Pyrolysis</span>
        </h1>
        <p className="mt-8 max-w-[62ch] text-lg md:text-xl text-char-700 leading-relaxed">
          Designed and operated by a team that ran this technology themselves before offering
          it for sale.
        </p>
        <div className="mt-8 max-w-[860px]" data-testid="equipment-configurations">
          <div className="mono-label">Available configurations</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {CONFIGURATIONS.map((c) => (
              <div key={c.capacity + c.line} className="border border-char-900/10 bg-paper p-6">
                <p className="font-display text-[28px] leading-none text-char-900">{c.capacity}</p>
                <p className="mt-3 text-sm text-char-700">{c.line}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#tp24-specs" data-testid="equipment-hero-cta-specs" className="btn-ember">
            View Technical Specifications
            <ArrowRight size={16} />
          </a>
          <Link
            to="/contact?reason=Technology%20Inquiry"
            data-testid="equipment-hero-cta-inquiry"
            className="btn-ghost-light"
          >
            Request Info
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>

    {/* SPECIFICATIONS */}
    <section
      id="tp24-specs"
      data-testid="equipment-specs"
      className="bg-char-900 text-paper py-20 lg:py-24 relative overflow-hidden scroll-mt-28"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="mono-label">Technical Specifications</div>
            <h2 className="font-display mt-4">
              TP 24 at a glance.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-char-600 text-lg leading-relaxed">
              A fully automatic, 24 TPD pyrolysis system engineered for continuous industrial
              operation and integrated with <TermRichText text="PLC/SCADA" /> control from day one.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Spec cards */}
          <div className="lg:col-span-12 space-y-3">
            {SPECS.map((s, i) => (
              <ScrollReveal key={s.label} delay={i * 60}>
                <div
                  data-testid={`spec-row-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border border-paper/15 hover:border-paper/35 transition-colors px-6 py-5 md:px-8 md:py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline"
                >
                  <div className="mono-label md:col-span-4">{s.label}</div>
                  <div className="font-display text-xl md:text-2xl tracking-tight md:col-span-8">
                    {s.value}
                    {s.footnote && (
                      <div className="mt-3 font-sans text-sm text-char-600 leading-relaxed max-w-[54ch]">
                        {s.footnote}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* PROOF & COMPLIANCE */}
    <section
      data-testid="equipment-proof"
      className="bg-paper py-20 lg:py-24 border-t border-char-900/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="mono-label">Proof &amp; Compliance</div>
            <h2 className="font-display mt-4 text-char-900">
              Beyond the spec sheet.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-char-700 text-lg leading-relaxed">
              What separates the TP 24 from a design on paper — certification, real-world
              operation, feedstock adaptability and emissions readiness for the geographies you're
              building in.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              id: "ebc",
              eyebrow: "Certification",
              icon: Award,
              accent: "moss",
              title: "Engineered to produce EBC-AgroOrganic grade biochar",
              body:
                "Suitable for use in organic agriculture, as defined under the European Biochar Certificate.",
            },
            {
              id: "operation",
              eyebrow: "Operational proof",
              icon: CheckCircle2,
              title: "Proven in commercial operation",
              body:
                "This system has been run in real production conditions, not just engineered on paper — with the same people who designed it responsible for keeping it running.",
            },
            {
              id: "feedstock",
              eyebrow: "Feedstock range",
              icon: Wheat,
              accent: "moss",
              title: "Engineered for a range of residues",
              body:
                "Engineered to process a range of agricultural and biomass residues. Currently validated output shown for cashew shell cake — talk to us about your specific feedstock.",
            },
            {
              id: "emissions",
              eyebrow: "Emissions",
              icon: Wind,
              title: "CPCB-compliant, adaptable elsewhere",
              body:
                "Designed and tested to meet Indian CPCB emission norms. For projects in other regions, our engineering team works with clients to assess and adapt the system to local emissions requirements, including EU and country-specific standards.",
            },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <ScrollReveal key={c.id} delay={i * 60}>
                <div
                  data-testid={`equipment-proof-card-${c.id}`}
                  className="h-full flex flex-col border border-char-900/10 hover:border-char-900/25 bg-paper transition-colors p-6 lg:p-8"
                >
                  <div className="flex items-center justify-between">
                    <span className="mono-label">{c.eyebrow}</span>
                    <Icon size={20} strokeWidth={1.6} className="text-char-700" aria-hidden="true" />
                  </div>
                  <h3 className="mt-6 font-display text-char-900 leading-tight">
                    <TermRichText text={c.title} />
                  </h3>
                  <p className="mt-4 text-char-700 text-[15px] leading-relaxed">
                    <TermRichText text={c.body} />
                  </p>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>

    {/* ADDITIONAL PRODUCTS */}
    {PRODUCTS.map((p) => (
      <section
        key={p.id}
        id={p.id}
        data-testid={`product-${p.id}`}
        className="bg-paper py-16 lg:py-20 border-t border-char-900/10 scroll-mt-28"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-14">
          <ScrollReveal className="md:col-span-5">
            <div className="mono-label">Technology</div>
            <h2 className="font-display mt-4 text-char-900">{p.title}</h2>
            {p.tag && (
              <div className="mt-3">
                <span
                  data-testid={`product-${p.id}-tag`}
                  className="inline-block font-sans text-[11px] uppercase tracking-[0.12em] text-char-700 border border-char-900/20 px-2 py-0.5 whitespace-nowrap"
                >
                  {p.tag}
                </span>
              </div>
            )}
          </ScrollReveal>
          <ScrollReveal delay={120} className="md:col-span-7">
            <p className="text-lg text-char-700 leading-relaxed">
              <TermRichText text={p.body} />
            </p>
            {p.specs.length > 0 && (
              <div className="mt-10 space-y-3">
                {p.specs.map((s) => (
                  <div
                    key={s.label}
                    data-testid={`spec-row-${p.id}-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="border border-char-900/10 bg-paper px-6 py-5 md:px-8 md:py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-baseline"
                  >
                    <div className="mono-label md:col-span-4">{s.label}</div>
                    <div className="font-display text-xl md:text-2xl tracking-tight text-char-900 md:col-span-8">
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollReveal>
        </div>
      </section>
    ))}

    {/* CTA */}
    <section
      data-testid="equipment-cta"
      className="bg-paper py-20 lg:py-24 border-t border-char-900/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
        <div>
          <div className="mono-label">Get in touch</div>
          <h2 className="font-display mt-4 text-char-900 max-w-[22ch]">
            Interested in our technologies?
          </h2>
          <p className="mt-6 text-char-700 text-lg leading-relaxed max-w-[52ch]">
            Send us your feedstock and site details and we'll come back with a full spec sheet,
            indicative timeline, and a call with the engineers who built it.
          </p>
        </div>
        <Link
          to="/contact?reason=Technology%20Inquiry"
          data-testid="equipment-cta-btn"
          className="btn-ember shrink-0"
        >
          Request Technical Specifications
          <ArrowUpRight size={18} />
        </Link>
      </div>
    </section>
  </div>
  );
};

export default Equipment;
